import { createTheme, buttonClasses } from '@mui/material';

declare module '@mui/material' {
  interface Color {
    25: string;
    950: string;
  }
}

const GREY = {
  25: '#F8F8F8',
  50: '#F1F1F1',
  100: '#E1E1E1',
  200: '#C8C8C8',
  300: '#ACACAC',
  400: '#919191',
  500: '#6E6E6E',
  600: '#404040',
  700: '#303030',
  800: '#292929',
  900: '#212121',
  950: '#141414',
} as const;

const PALETTE = {
  primary: { main: '#33691E', dark: '#003D00', light: '#629749' },
  secondary: { main: GREY[500], dark: GREY[900], light: GREY[400] },
  grey: GREY,
  error: { main: '#B40000' },
  warning: { main: '#DE6B00' },
  success: { main: '#33691E' },
} as const;

const geopardTheme = createTheme({
  palette: PALETTE,
  breakpoints: { values: { xs: 0, sm: 630, md: 1160, lg: 1280, xl: 1536 } },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '2px',
          boxShadow: 'none',
          [`&.${buttonClasses.colorPrimary}`]: {
            [`&.${buttonClasses.contained}`]: {
              '&:hover': {
                backgroundColor: PALETTE.primary.light,
                boxShadow: 'none',
              },
            },
            [`&.${buttonClasses.outlined}`]: {
              border: `1px solid ${PALETTE.primary.main}`,
              '&:hover': {
                backgroundColor: PALETTE.grey[25],
                boxShadow: 'none',
              },
            },
          },
          [`&.${buttonClasses.focusVisible}`]: { boxShadow: 'none' },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
          fontSize: '14px',
          lineHeight: '20px',
        },
      },
    },
  },
});

geopardTheme.typography.h1 = {
  ...geopardTheme.typography.h1,
  color: geopardTheme.palette.secondary.dark,
  fontWeight: 500,
  fontSize: '48px',
  lineHeight: '56px',
  [geopardTheme.breakpoints.between('sm', 'md')]: {
    fontSize: '36px',
    lineHeight: '44px',
  },
  [geopardTheme.breakpoints.down('sm')]: {
    fontSize: '24px',
    lineHeight: '28px',
  },
};

geopardTheme.typography.h2 = {
  ...geopardTheme.typography.h2,
  color: geopardTheme.palette.secondary.dark,
  fontWeight: 500,
  fontSize: '32px',
  lineHeight: '36px',
  [geopardTheme.breakpoints.down('md')]: { fontSize: '28px' },
};

geopardTheme.typography.h3 = {
  ...geopardTheme.typography.h3,
  color: geopardTheme.palette.secondary.light,
  fontWeight: 400,
  fontSize: '24px',
  lineHeight: '36px',
  [geopardTheme.breakpoints.between('sm', 'md')]: {
    fontSize: '20px',
    lineHeight: '28px',
  },
  [geopardTheme.breakpoints.down('sm')]: {
    fontSize: '16px',
    lineHeight: '19px',
  },
};

geopardTheme.typography.h4 = {
  ...geopardTheme.typography.h4,
  color: geopardTheme.palette.secondary.dark,
  fontWeight: 500,
  fontSize: '20px',
  lineHeight: '28px',
};

geopardTheme.typography.body1 = {
  ...geopardTheme.typography.body1,
  color: geopardTheme.palette.secondary.main,
  fontSize: '14px',
  lineHeight: '20px',
};

geopardTheme.typography.body2 = {
  ...geopardTheme.typography.body2,
  color: geopardTheme.palette.secondary.dark,
  fontSize: '14px',
  lineHeight: '20px',
};

geopardTheme.typography.subtitle1 = {
  ...geopardTheme.typography.subtitle1,
  color: geopardTheme.palette.secondary.dark,
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
};

export default geopardTheme;
